import type { InternalApiKeysTranslations } from "../interfaces/internalApiKeys";

export const internalApiKeysTranslations: InternalApiKeysTranslations = {
  page_title: "Internal keys",
  page_subtitle:
    "Keys that let Coloca's own systems, such as the CRM, use the internal API without a user and password. Every change asks for your verification code, except turning a key off or revoking it.",
  action_refresh: "Refresh",
  action_new: "New key",
  action_retry: "Retry",
  surface_off: "The internal API is off in this environment. Keys do not answer until it is turned on.",
  access_failed: "We could not check your access to internal keys. Try again in a moment.",

  empty_title: "No internal keys yet",
  empty_description: "Create one so the CRM stops signing in with a user and password.",

  status_ACTIVE: "Active",
  status_DISABLED: "Off",
  status_NO_IP: "No IP",
  status_EXPIRED: "Expired",
  status_REVOKED: "Revoked",

  row_all_organizations: "All organizations",
  row_organizations_one: "1 organization",
  row_organizations_other: "{{count}} organizations",
  row_permissions_all: "Permissions: all",
  row_permissions: "Permissions: {{list}}",
  row_no_ip: "No allowed IP",
  row_ips: "IP {{ips}}",
  row_no_ip_low_risk: "No IP (cardholders from any IP)",
  row_ips_low_risk: "IP {{ips}} · cardholders from any IP",
  row_expires: "Expires on {{date}}",
  row_expired: "Expired on {{date}}",
  row_revoked: "Revoked on {{date}}",
  row_last_used: "Last used {{date}} from {{ip}}",
  row_never_used: "Not used yet",
  row_rotation_grace: "The previous secret works until {{date}}",
  switch_label: "Turn {{name}} on or off",
  switch_needs_ip: "Add an IP to turn it on",
  switch_needs_expiry: "Extend the expiry to turn it on",
  menu_label: "Actions for {{name}}",

  action_edit: "Edit",
  action_edit_ips: "Edit IPs",
  action_rotate: "Change the secret",
  action_usage: "View usage",
  action_revoke: "Revoke",
  cancel: "Cancel",

  toast_created: "Key created",
  toast_updated: "Changes saved",
  toast_ips_updated: "IPs saved",
  toast_enabled: "Key turned on",
  toast_disabled: "Key turned off",
  toast_rotated: "Secret changed",
  toast_revoked: "Key revoked",

  revoke_title: "Revoke {{name}}?",
  revoke_description:
    "It stops working at once and cannot be used again. The system using it gets 404 until it has a new key.",
  revoke_confirm: "Revoke",
  rotate_title: "Change the secret of {{name}}?",
  rotate_description_grace:
    "A new secret is generated. The previous one keeps working for {{hours}} hours so the system using it can switch.",
  rotate_description_now: "A new secret is generated and the previous one stops working at once.",
  rotate_retire_now_label: "The secret leaked: stop accepting the previous one now",
  rotate_retire_now_hint: "The system using the key fails until it gets the new secret.",
  rotate_confirm: "Change the secret",

  token_title_created: "Copy the key for {{name}} now",
  token_title_rotated: "Copy the new secret for {{name}} now",
  token_warning:
    "It is not shown again. Hand it over through a secrets or password manager, never chat or email.",
  token_copy: "Copy",
  token_copied: "Copied",
  token_dismiss: "I saved it",

  form_title_create: "New internal key",
  form_title_edit: "Edit {{name}}",
  form_description: "What the key can do, on which organizations and from which IP.",
  field_name: "Name",
  field_name_placeholder: "CRM",
  field_description: "What it is for (optional)",
  section_permissions: "Permissions",
  all_permissions_label: "All permissions",
  all_permissions_hint: "Includes the ones added to the internal API later.",
  permission_operation_payments_read: "View payment orders",
  permission_operation_payments_create: "Create payment orders",
  permission_operation_payments_cancel: "Cancel pending orders",
  permission_clients_read: "View clients",
  permission_clients_write: "Create and edit clients",
  permission_client_accounts_write: "Register client accounts",
  permission_cardholders_write: "Onboard cardholders (and retry their registration)",
  section_organizations: "Organizations",
  all_organizations_label: "All organizations",
  all_organizations_hint: "The key can create and read orders and clients of any organization.",
  organizations_add: "Add organization",
  organizations_add_hint: "Outside these organizations the key gets 404.",
  organizations_remove: "Remove {{name}}",
  section_ips: "Allowed IPs",
  ips_hint:
    "Exact public IPs, up to 10. Without an IP the key can only be turned on if it allows cardholder onboarding from any IP.",
  allow_without_ip_label: "Allow cardholder onboarding from any IP",
  allow_without_ip_hint:
    "Only for onboarding cardholders and retrying their registration. Payments and clients still require an IP on the list.",
  ip_label: "IP",
  ip_placeholder: "54.213.10.45",
  ip_label_placeholder: "Label (optional)",
  ip_add: "Add",
  ip_remove: "Remove {{ip}}",
  ip_invalid: "Type a valid IP (IPv4 or IPv6), no ranges.",
  ip_duplicate: "That IP is already on the list.",
  ips_dialog_title: "IPs of {{name}}",
  ips_dialog_submit: "Save IPs",
  ips_dialog_turns_off:
    "With no IPs the key turns off when you save, and it cannot be turned on until you add one.",
  ips_dialog_only_cardholders:
    "With no IPs the key can only onboard cardholders. Payments and clients will get 404.",
  section_limits: "Expiry and limit",
  field_expires_on: "Works until",
  field_expires_hint: "At most {{days}} days from today.",
  field_rate_limit: "Requests per minute",
  field_rate_limit_hint: "Past the limit it answers 429 until the next minute.",
  error_name: "Type a name of at least 3 characters.",
  error_permissions: "Pick at least one permission, or all of them.",
  error_organizations: "Pick at least one organization, or all of them.",
  error_expiry: "Pick until when it works.",
  error_rate_limit: "Type a number between 1 and 6000.",
  error_ips: "Up to 10 IPs.",
  form_submit_create: "Create key",
  form_submit_edit: "Save changes",

  two_factor_required: "Creating or changing internal keys requires two-step verification.",
  two_factor_challenge: "Type the code from your authenticator app to confirm.",

  usage_title: "Usage of {{name}}",
  usage_description: "Every request that arrived with this key, refused ones included.",
  usage_empty: "No requests yet",
  usage_status: "HTTP {{code}}",
  outcome_OK: "Accepted",
  outcome_SURFACE_OFF: "Internal API off",
  outcome_ORIGIN_REJECTED: "Came from a browser",
  outcome_MISSING_KEY: "No key",
  outcome_MALFORMED_KEY: "Malformed key",
  outcome_WRONG_ENV: "Key from another environment",
  outcome_UNKNOWN_KEY: "Unknown key",
  outcome_BAD_SECRET: "Wrong secret",
  outcome_REVOKED: "Key revoked",
  outcome_DISABLED: "Key off",
  outcome_EXPIRED: "Key expired",
  outcome_IP_BLOCKED: "IP not allowed",
  outcome_SERVICE_USER_INACTIVE: "Service user inactive",
  outcome_FORBIDDEN: "Not allowed for that operation",
  outcome_RATE_LIMITED: "Over the per-minute limit",
  outcome_ERROR: "Error while checking the key",
};
